import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  apiLogin,
  apiSignup,
  apiMe,
  apiLogout,
  setToken,
  getToken,
  getFavoriteIds,
  addFavorite,
  removeFavorite,
} from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon, obj = authed
  const [authModalMode, setAuthModalMode] = useState(null); // "login" | "signup" | null
  const [afterAuth, setAfterAuth] = useState(null); // function to run after successful auth
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  const openAuth = useCallback((mode = "login", cb = null) => {
    setAuthModalMode(mode);
    setAfterAuth(() => cb);
  }, []);
  const closeAuth = useCallback(() => {
    setAuthModalMode(null);
    setAfterAuth(null);
  }, []);

  const loadFavorites = useCallback(async () => {
    try {
      const ids = await getFavoriteIds();
      setFavoriteIds(new Set(ids));
    } catch {
      setFavoriteIds(new Set());
    }
  }, []);

  // Check session on mount
  useEffect(() => {
    const t = getToken();
    if (!t) {
      setUser(null);
      return;
    }
    apiMe()
      .then((u) => {
        setUser(u);
        loadFavorites();
      })
      .catch(() => {
        setToken(null);
        setUser(null);
      });
  }, [loadFavorites]);

  const login = useCallback(
    async ({ email, password }) => {
      const { user: u, token } = await apiLogin({ email, password });
      setToken(token);
      setUser(u);
      await loadFavorites();
      if (typeof afterAuth === "function") {
        try { afterAuth(); } catch { /* callback error should not break login */ }
      }
      setAuthModalMode(null);
      setAfterAuth(null);
      return u;
    },
    [afterAuth, loadFavorites]
  );

  const signup = useCallback(
    async ({ name, email, password }) => {
      const { user: u, token } = await apiSignup({ name, email, password });
      setToken(token);
      setUser(u);
      await loadFavorites();
      if (typeof afterAuth === "function") {
        try { afterAuth(); } catch { /* callback error should not break signup */ }
      }
      setAuthModalMode(null);
      setAfterAuth(null);
      return u;
    },
    [afterAuth, loadFavorites]
  );

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* stateless */ }
    setToken(null);
    setUser(null);
    setFavoriteIds(new Set());
  }, []);

  /**
   * requireAuth: if signed in, runs cb immediately.
   * If not signed in, opens login modal; cb will run after successful login.
   */
  const requireAuth = useCallback(
    (cb) => {
      if (user) {
        cb?.();
        return true;
      }
      openAuth("login", cb);
      return false;
    },
    [user, openAuth]
  );

  const toggleFavorite = useCallback(
    async (photo) => {
      if (!user) {
        openAuth("login", () => toggleFavorite(photo));
        return;
      }
      const id = photo.id;
      const wasFav = favoriteIds.has(id);
      // optimistic
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.delete(id);
        else next.add(id);
        return next;
      });
      try {
        if (wasFav) await removeFavorite(id);
        else await addFavorite(photo);
      } catch {
        // rollback
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          if (wasFav) next.add(id);
          else next.delete(id);
          return next;
        });
      }
    },
    [user, favoriteIds, openAuth]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthed: !!user,
      loading: user === undefined,
      login,
      signup,
      logout,
      requireAuth,
      authModalMode,
      openAuth,
      closeAuth,
      favoriteIds,
      isFavorited: (id) => favoriteIds.has(id),
      toggleFavorite,
      reloadFavorites: loadFavorites,
    }),
    [
      user,
      login,
      signup,
      logout,
      requireAuth,
      authModalMode,
      openAuth,
      closeAuth,
      favoriteIds,
      toggleFavorite,
      loadFavorites,
    ]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
